import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Stack,
  Tabs,
  Tab,
  Fade,
} from "@mui/material";
import { AmericanaMatch, AmericanaRound, Player, Tournament } from "../types";
import { apiRequest } from "../config/api";
import { useOrganization } from "../contexts/OrganizationContext";
import { extractImagePalette, ImagePalette } from "../utils/extractImagePalette";
import padelEagleLogo from "../img/padelEagle.png";
import splLogo from "../img/SPL_Logo.png";

const AmericanaTournamentDisplay: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { currentOrg } = useOrganization();

  // Mirrors TournamentDisplay.tsx / PublicLayout.tsx theming
  const isPadelEagle = currentOrg?.slug === "padel-eagle";
  const orgLogo = currentOrg?.branding?.logo || (isPadelEagle ? padelEagleLogo : splLogo);
  const orgColor = isPadelEagle ? "#743444" : "#1976d2";
  const orgAccent = isPadelEagle ? "#E49421" : "#e3f2fd";
  const orgName = isPadelEagle ? "Padel Eagle" : (currentOrg?.name || "SPL");

  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [players, setPlayers] = useState<Player[]>([]);
  const [currentTab, setCurrentTab] = useState(0);
  const [posterColors, setPosterColors] = useState<ImagePalette | null>(null);

  const fetchTournament = useCallback(async () => {
    try {
      const r = await apiRequest(`/api/tournaments/${id}`);
      const data = await r.json();
      if (r.ok) setTournament(data);
    } catch {
      // keep last known state on transient failure
    }
  }, [id]);

  const fetchPlayers = useCallback(async () => {
    try {
      const r = await apiRequest("/api/players");
      const data = await r.json();
      if (r.ok) setPlayers(data);
    } catch {
      // keep last known state on transient failure
    }
  }, []);

  useEffect(() => {
    fetchTournament();
    fetchPlayers();
    const interval = setInterval(() => {
      fetchTournament();
      fetchPlayers();
    }, 10000);
    return () => clearInterval(interval);
  }, [fetchTournament, fetchPlayers]);

  const posterImageUrl = tournament?.posterImageUrl;

  useEffect(() => {
    if (!posterImageUrl) {
      setPosterColors(null);
      return;
    }
    let cancelled = false;
    extractImagePalette(posterImageUrl).then(palette => {
      if (!cancelled) setPosterColors(palette);
    });
    return () => {
      cancelled = true;
    };
  }, [posterImageUrl]);

  const themeColor = posterColors?.primary || orgColor;
  const themeAccent = posterColors?.accent || orgAccent;

  const playerName = useMemo(() => {
    const map = new Map(players.map(p => [p.id, p.name]));
    return (playerId: string) => map.get(playerId) || "Unknown";
  }, [players]);

  const handicapTypeMap = useMemo(() => {
    return new Map((tournament?.americanaHandicapTypes || []).map(h => [h.id, h]));
  }, [tournament?.americanaHandicapTypes]);

  const sortedRounds = useMemo(() => {
    return [...(tournament?.americanaRounds || [])].sort((a, b) => a.roundNumber - b.roundNumber);
  }, [tournament?.americanaRounds]);

  const maxCourts = sortedRounds.reduce((max, round) => Math.max(max, round.matches.length), 0);

  const renderHandicaps = (ids: string[] | undefined) =>
    (ids || []).map(hid => handicapTypeMap.get(hid)).filter(Boolean).map(h => (
      <Box
        key={h!.id}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          px: 0.75,
          py: 0.25,
          borderRadius: 4,
          bgcolor: "rgba(255,255,255,0.15)",
        }}
      >
        <span style={{ fontSize: 15, lineHeight: 1 }}>{h!.emoji}</span>
        <Typography variant="caption" sx={{ color: "#fff", fontWeight: 700, whiteSpace: "nowrap" }}>
          {h!.label}
        </Typography>
      </Box>
    ));

  const renderMatchCard = (match: AmericanaMatch, compact = false) => {
    const pair1Won = match.isCompleted && match.score.winnerPair === 1;
    const pair2Won = match.isCompleted && match.score.winnerPair === 2;

    return (
      <Card
        sx={{
          width: "100%",
          height: "100%",
          bgcolor: match.isCompleted ? "rgba(46, 125, 50, 0.25)" : "rgba(116,52,68, 0.5)",
          border: match.isCompleted ? "2px solid #4caf50" : `2px solid ${themeAccent}`,
        }}
      >
        <CardContent sx={{ p: compact ? 1.25 : 2, "&:last-child": { pb: compact ? 1.25 : 2 } }}>
          <Typography variant="caption" sx={{ color: themeAccent, fontWeight: "bold" }}>
            Court {match.courtNumber}
          </Typography>

          <Stack direction="row" alignItems="center" flexWrap="wrap" spacing={0.5} sx={{ mt: 0.5, rowGap: 0.5 }}>
            <Typography
              variant={compact ? "body1" : "h6"}
              sx={{ color: pair1Won ? "#8bc34a" : "#fff", fontWeight: "bold" }}
            >
              {playerName(match.pair1.player1Id)} / {playerName(match.pair1.player2Id)}
            </Typography>
            {renderHandicaps(match.pair1HandicapIds)}
          </Stack>

          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)" }}>
            vs
          </Typography>

          <Stack direction="row" alignItems="center" flexWrap="wrap" spacing={0.5} sx={{ rowGap: 0.5 }}>
            <Typography
              variant={compact ? "body1" : "h6"}
              sx={{ color: pair2Won ? "#8bc34a" : "#fff", fontWeight: "bold" }}
            >
              {playerName(match.pair2.player1Id)} / {playerName(match.pair2.player2Id)}
            </Typography>
            {renderHandicaps(match.pair2HandicapIds)}
          </Stack>

          <Typography
            variant={compact ? "h6" : "h5"}
            sx={{ color: "#fff", fontWeight: "bold", textAlign: "center", mt: 1 }}
          >
            {match.isCompleted ? `${match.score.pair1Games} - ${match.score.pair2Games}` : "Not yet played"}
          </Typography>
        </CardContent>
      </Card>
    );
  };

  const renderRound = (round: AmericanaRound) => {
    const matches = [...round.matches].sort((a, b) => a.courtNumber - b.courtNumber);
    return (
      <Fade in timeout={500}>
        <Grid container spacing={2}>
          {matches.map(match => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={match.id}>
              {renderMatchCard(match)}
            </Grid>
          ))}
        </Grid>
      </Fade>
    );
  };

  const renderFullSchedule = () => (
    <Fade in timeout={500}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: `140px repeat(${Math.max(maxCourts, 1)}, 1fr)`,
          gap: 1,
        }}
      >
        <Box sx={{ p: 2, bgcolor: themeColor, color: "#fff", fontWeight: "bold", textAlign: "center", borderRadius: 1 }}>
          Round
        </Box>
        {Array.from({ length: Math.max(maxCourts, 1) }, (_, i) => (
          <Box key={i} sx={{ p: 2, bgcolor: themeColor, color: "#fff", fontWeight: "bold", textAlign: "center", borderRadius: 1 }}>
            Court {i + 1}
          </Box>
        ))}

        {sortedRounds.map(round => (
          <React.Fragment key={round.roundNumber}>
            <Box
              sx={{
                p: 2,
                bgcolor: "rgba(255,255,255,0.1)",
                color: themeAccent,
                fontWeight: "bold",
                fontSize: "1.1rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 1,
              }}
            >
              Round {round.roundNumber}
            </Box>
            {Array.from({ length: Math.max(maxCourts, 1) }, (_, i) => {
              const courtNumber = i + 1;
              const match = round.matches.find(m => m.courtNumber === courtNumber);
              return (
                <Box key={courtNumber} sx={{ minHeight: 120, borderRadius: 1 }}>
                  {match ? renderMatchCard(match, true) : (
                    <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "rgba(0,0,0,0.3)", borderRadius: 1 }}>
                      <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.3)" }}>—</Typography>
                    </Box>
                  )}
                </Box>
              );
            })}
          </React.Fragment>
        ))}
      </Box>
    </Fade>
  );

  if (!tournament) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#1a1a1a",
          backgroundColor: isPadelEagle ? "#2E151B" : "#2d1b26",
        }}
      >
        <Stack alignItems="center" spacing={3}>
          <img src={orgLogo} alt={orgName} style={{ height: 150 }} />
          <Typography variant="h3" sx={{ color: orgAccent }}>
            Loading...
          </Typography>
        </Stack>
      </Box>
    );
  }

  const tabPanels = [
    ...sortedRounds.map(round => ({
      key: `round-${round.roundNumber}`,
      label: `Round ${round.roundNumber}`,
      render: () => renderRound(round),
    })),
    { key: "full-schedule", label: "Full Schedule", render: renderFullSchedule },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#1a1a1a",
        color: "#fff",
        p: 4,
        backgroundColor: isPadelEagle ? "#2E151B" : "#2d1b26",
      }}
    >
      <Box sx={{ maxWidth: "1920px", mx: "auto" }}>
        {posterImageUrl ? (
          <Box
            sx={{
              mb: 3,
              borderRadius: 3,
              overflow: "hidden",
              position: "relative",
              boxShadow: "none",
            }}
          >
            {/* Blurred cover backdrop fills the banner so there's no letterbox gap */}
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                zIndex: 0,
                backgroundImage: `url(${posterImageUrl})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                filter: "blur(30px) brightness(0.5)",
                transform: "scale(1.15)",
              }}
            />
            {/* Full, uncropped poster — sets the container's height */}
            <Box
              component="img"
              src={posterImageUrl}
              alt={tournament.name}
              sx={{
                position: "relative",
                zIndex: 1,
                width: "100%",
                maxHeight: { xs: 260, md: 380 },
                objectFit: "contain",
                display: "block",
                mx: "auto",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                zIndex: 2,
                background: "linear-gradient(180deg, rgba(8,18,38,0) 55%, rgba(10,10,10,0.85) 100%)",
              }}
            />
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{ position: "absolute", left: 0, right: 0, bottom: 0, zIndex: 3, p: { xs: 2, md: 3 } }}
            >
              <img src={orgLogo} alt={orgName} style={{ height: 56 }} />
              <Typography variant="h3" sx={{ fontWeight: "bold", color: "#fff", fontSize: { xs: "1.6rem", md: "2.4rem" }, textShadow: "0 2px 12px rgba(0,0,0,0.6)" }}>
                {tournament.name}
              </Typography>
            </Stack>
          </Box>
        ) : (
          <Stack alignItems="center" spacing={1} sx={{ mb: 3 }}>
            <img src={orgLogo} alt={orgName} style={{ height: 80 }} />
            <Typography variant="h4" sx={{ fontWeight: "bold", color: themeAccent }}>
              {tournament.name}
            </Typography>
          </Stack>
        )}

        <Tabs
          value={currentTab}
          onChange={(_, newValue) => setCurrentTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
          TabIndicatorProps={{ style: { backgroundColor: themeAccent } }}
        >
          {tabPanels.map(t => (
            <Tab key={t.key} label={t.label} sx={{ fontSize: "1.1rem", color: "rgba(255,255,255,0.7)", "&.Mui-selected": { color: themeAccent } }} />
          ))}
        </Tabs>

        {tabPanels[currentTab]?.render()}
      </Box>
    </Box>
  );
};

export default AmericanaTournamentDisplay;
